<!-- src/views/PettyCashView.vue -->

<template>
  <div class="section coredeck-page-section">
    <div class="row">
      <div class="col s12">
        <div class="card">
          <div class="card-content blue-grey darken-4 white-text">
            <div class="row" style="margin-bottom: 0">
              <div class="col s12 m6">
                <span class="card-title"> Petty Cash </span>
              </div>

              <div class="col s12 m6 right-align">
                <button
                  class="btn-small blue-grey"
                  :disabled="loading || venueModeLoading || printingPettyCash || !ready"
                  @click="reloadAll"
                >
                  Refresh week
                </button>
              </div>
            </div>
          </div>

          <div class="card-content">
            <div
              v-if="!ready"
              class="card-panel amber lighten-4 brown-text text-darken-4"
            >
              Waiting for CoreDeck context.

              <div style="margin-top: 8px">
                <strong> Current context: </strong>

                venueId="{{ venueId || "—" }}", tradingDate="{{ tradingDate || "—" }}",
                weekEnding="{{ weekEnding || "—" }}"
              </div>
            </div>

            <div
              v-else-if="venueModeLoading"
              class="card-panel blue-grey lighten-5 blue-grey-text text-darken-4"
            >
              Loading venue Petty Cash settings...
            </div>

            <template v-else>
              <div
                v-if="venueModeError"
                class="card-panel amber lighten-4 brown-text text-darken-4"
              >
                {{ venueModeError }}

                <div style="margin-top: 6px">
                  Petty Cash is using the standard cash venue mode.
                </div>
              </div>

              <div
                v-if="isCashless"
                class="card-panel teal lighten-5 teal-text text-darken-4"
              >
                <div class="cashless-heading">
                  <i class="material-icons"> credit_card </i>

                  <strong> Cashless venue </strong>
                </div>

                <div style="margin-top: 8px">
                  A receipt must be uploaded for every Petty Cash expense. Use the week
                  controls below to move between Petty Cash weeks.
                </div>
              </div>

              <RotaToolbar
                v-if="isCashless"
                :weekRangeText="weekRangeText"
                :weekEnding="activeWeekEnding"
                :canGoNext="canGoNext"
                :busy="
                  loading ||
                  venueModeLoading ||
                  printingPettyCash ||
                  Boolean(approvalBusyId)
                "
                :showPrintWeeklyPack="false"
                :showDateRangePicker="false"
                @prev="goPrevWeek"
                @next="goNextWeek"
              />

              <PettyCashForm
                :loading="loading || printingPettyCash || Boolean(approvalBusyId)"
                :categories="categories"
                :cashless="isCashless"
                @save="saveEntry"
              />

              <div v-if="error" class="card-panel red lighten-4 red-text text-darken-4">
                {{ error }}
              </div>

              <div class="divider" style="margin: 16px 0"></div>

              <h6 style="margin-top: 0">
                <template v-if="isCashless"> Petty cash for selected week </template>

                <template v-else> Unclaimed petty cash </template>

                <span class="grey-text text-darken-1" style="font-size: 0.9rem">
                  (
                  {{ activeWeekStartISO }}
                  →
                  {{ activeWeekEnding }}
                  )

                  <template v-if="!isCashless">
                    — includes previous unclaimed items
                  </template>
                </span>
              </h6>

              <div
                class="card-panel blue-grey lighten-5 blue-grey-text text-darken-4"
                style="margin-top: 12px"
              >
                <strong>
                  {{
                    isCashless ? "Total for selected week:" : "Total awaiting claim back:"
                  }}
                </strong>

                £{{ displayTotal.toFixed(2) }}
              </div>

              <div v-if="loading" class="progress">
                <div class="indeterminate"></div>
              </div>

              <PettyCashCashlessTable
                v-else-if="isCashless"
                :rows="displayRows"
                :isOperations="isOperations"
                :approvalBusyId="approvalBusyId"
                :reportBusy="printingPettyCash"
                :reportDisabled="loading || venueModeLoading || !activeWeekEnding"
                @toggle-claimed="onToggleClaimed"
                @toggle-approval="onToggleApproval"
                @print-report="printPettyCashReport"
              />

              <PettyCashTable v-else :rows="rows" @toggle-claimed="onToggleClaimed" />
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";

import { doc, getDoc } from "firebase/firestore";

import { httpsCallable } from "firebase/functions";

import { auth, authReady, db, functions } from "@/firebase";

import RotaToolbar from "@/components/rota/RotaToolbar.vue";

import PettyCashForm from "@/components/pettyCash/PettyCashForm.vue";

import PettyCashTable from "@/components/pettyCash/PettyCashTable.vue";

import PettyCashCashlessTable from "@/components/pettyCash/PettyCashCashlessTable.vue";

import {
  pettyCashWeekResolveRows,
  pettyCashWeekUpsert,
} from "@/scripts/sqlApiHelper/pettyCashApi";

import { toISODate, addDaysISO } from "@/scripts/sharedHelpers/formatter";

import { useVenueContext } from "@/scripts/useScripts/useVenueContext";

const generateWeeklyPack = httpsCallable(functions, "generateWeeklyPack");

function showToast(message) {
  const text = String(message || "").trim();

  if (!text) {
    return;
  }

  const M = window.M;

  if (M && typeof M.toast === "function") {
    M.toast({
      html: text,

      displayLength: 3000,
    });

    return;
  }

  console.log("[PettyCash]", text);
}

const isOperations = ref(false);

async function resolveOperationsAccess() {
  isOperations.value = false;

  try {
    await authReady;

    const user = auth.currentUser;

    if (!user) {
      return;
    }

    const userSnap = await getDoc(doc(db, "users", user.uid));

    if (!userSnap.exists()) {
      return;
    }

    const data = userSnap.data() || {};

    const role = String(data.role || "")
      .trim()
      .toLowerCase();

    isOperations.value =
      role === "operations" ||
      data.operations === true ||
      data.isOperations === true ||
      data.is_operations === true;
  } catch (e) {
    console.error("[PettyCashView] Failed to resolve Operations access:", e);

    isOperations.value = false;
  }
}

const categories = [
  "FOOD PURCHASES",
  "LIQUOR PURCHASES",
  "BAR FRUIT/COCKTAILS",
  "BAR ITEMS",
  "SNACKS FOR RESALE",
  "MUSIC/ENTERTAINING",
  "POSTAGE",
  "STATIONERY",
  "REPAIRS",
  "CLEANING",
  "SECURITY COSTS",
  "REFUNDS (IF APPLICABLE)",
  "MARKETING",
  "FUEL",
  "VAN HIRE",
];

const venueContext = useVenueContext();

function clean(value) {
  return String(value ?? "").trim();
}

function getContextValue(...keys) {
  for (const key of keys) {
    const maybeRef = venueContext?.[key];

    const value =
      maybeRef && typeof maybeRef === "object" && "value" in maybeRef
        ? maybeRef.value
        : maybeRef;

    const cleaned = clean(value);

    if (cleaned) {
      return cleaned;
    }
  }

  return "";
}

const venueId = computed(() => {
  return getContextValue("venueId") || clean(localStorage.getItem("venue"));
});

const tradingDate = computed(() => {
  return (
    getContextValue("tradingDate", "businessDate") ||
    clean(localStorage.getItem("tradingDate")) ||
    clean(localStorage.getItem("businessDate"))
  );
});

const weekEnding = computed(() => {
  return (
    getContextValue("weekEnding", "weekEndingDate") ||
    clean(localStorage.getItem("weekEnding"))
  );
});

const ready = computed(() => {
  return Boolean(venueId.value && tradingDate.value && weekEnding.value);
});

const venueModeLoading = ref(false);

const venueModeError = ref("");

const isCashless = ref(false);

async function loadVenueMode() {
  venueModeError.value = "";

  isCashless.value = false;

  const id = clean(venueId.value);

  if (!id) {
    return;
  }

  venueModeLoading.value = true;

  try {
    const venueSnap = await getDoc(doc(db, "venues", id));

    if (!venueSnap.exists()) {
      throw new Error(`Venue "${id}" could not be found.`);
    }

    const data = venueSnap.data() || {};

    isCashless.value = data.is_cashless === true;
  } catch (e) {
    console.error("[PettyCashView] Failed to load cashless setting:", e);

    isCashless.value = false;

    venueModeError.value = e?.message || "Could not read the venue cashless setting.";
  } finally {
    venueModeLoading.value = false;
  }
}

const cashlessWeekEnding = ref("");

const activeWeekEnding = computed(() => {
  if (isCashless.value) {
    return clean(cashlessWeekEnding.value) || clean(weekEnding.value);
  }

  return clean(weekEnding.value);
});

const activeWeekStartISO = computed(() => {
  if (!activeWeekEnding.value) {
    return "";
  }

  return addDaysISO(activeWeekEnding.value, -6);
});

function validISODate(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(clean(value));
}

function parseISO(value) {
  const iso = clean(value);

  if (!validISODate(iso)) {
    return null;
  }

  const [year, month, day] = iso.split("-").map(Number);

  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

function dateToISO(date) {
  if (!date || Number.isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();

  const month = String(date.getMonth() + 1).padStart(2, "0");

  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function addDays(date, days) {
  const next = new Date(date);

  next.setDate(next.getDate() + Number(days || 0));

  return next;
}

function addWeeks(date, weeks) {
  return addDays(date, Number(weeks || 0) * 7);
}

function formatUK(date) {
  const day = String(date.getDate()).padStart(2, "0");

  const month = date.toLocaleString("en-GB", {
    month: "short",
  });

  return `${day} ${month} ${date.getFullYear()}`;
}

function weekEndingSundayForDate(date) {
  const working = new Date(date);

  const daysUntilSunday = (7 - working.getDay()) % 7;

  working.setDate(working.getDate() + daysUntilSunday);

  return dateToISO(working);
}

const thisWeekEnding = computed(() => weekEndingSundayForDate(new Date()));

const maxWeekEnding = computed(() => {
  const current = parseISO(thisWeekEnding.value);

  return current ? dateToISO(addWeeks(current, 10)) : "";
});

const weekRangeText = computed(() => {
  const end = parseISO(activeWeekEnding.value);

  if (!end) {
    return "";
  }

  const start = addDays(end, -6);

  return `${formatUK(start)} – ${formatUK(end)}`;
});

const canGoNext = computed(() => {
  if (!isCashless.value) {
    return false;
  }

  const current = parseISO(activeWeekEnding.value);

  if (!current) {
    return false;
  }

  return dateToISO(addWeeks(current, 1)) <= maxWeekEnding.value;
});

async function goPrevWeek() {
  const current = parseISO(activeWeekEnding.value);

  if (!current || loading.value || printingPettyCash.value || approvalBusyId.value) {
    return;
  }

  cashlessWeekEnding.value = dateToISO(addWeeks(current, -1));

  await reloadFromApi();
}

async function goNextWeek() {
  if (
    !canGoNext.value ||
    loading.value ||
    printingPettyCash.value ||
    approvalBusyId.value
  ) {
    return;
  }

  const current = parseISO(activeWeekEnding.value);

  if (!current) {
    return;
  }

  cashlessWeekEnding.value = dateToISO(addWeeks(current, 1));

  await reloadFromApi();
}

const loading = ref(false);

const printingPettyCash = ref(false);

const approvalBusyId = ref("");

const error = ref("");

const rows = ref([]);

let loadSequence = 0;

function toBool(value) {
  if (value === true) {
    return true;
  }

  if (value === false) {
    return false;
  }

  if (typeof value === "string") {
    return value.trim().toLowerCase() === "true";
  }

  return Boolean(value);
}

function isClaimedBack(row) {
  return toBool(row?.is_claimed_back) || toBool(row?.is_calimed_back);
}

function computeUnclaimedTotal(list) {
  return (list || []).reduce((sum, row) => {
    if (isClaimedBack(row)) {
      return sum;
    }

    return sum + Number(row?.amount || 0);
  }, 0);
}

function sourceWeekEndingForRow(row) {
  return clean(
    row?._source_week_ending_date ||
      row?.source_week_ending_date ||
      row?.week_ending_date ||
      ""
  );
}

function cashlessRowBelongsToSelectedWeek(row) {
  const selectedWeek = activeWeekEnding.value;

  const sourceWeek = sourceWeekEndingForRow(row);

  if (sourceWeek) {
    return sourceWeek === selectedWeek;
  }

  const expenseDate = clean(row?.expense_date).slice(0, 10);

  return (
    validISODate(expenseDate) &&
    expenseDate >= activeWeekStartISO.value &&
    expenseDate <= selectedWeek
  );
}

const displayRows = computed(() => {
  if (!isCashless.value) {
    return rows.value;
  }

  return (rows.value || []).filter(cashlessRowBelongsToSelectedWeek);
});

const displayTotal = computed(() => computeUnclaimedTotal(displayRows.value));

function shouldPushTotalToApp() {
  return !isCashless.value || activeWeekEnding.value === clean(weekEnding.value);
}

function pushPettyCashTotalToApp(total) {
  const rounded = Number(Number(total || 0).toFixed(2));

  localStorage.setItem("pettyCash", String(rounded));

  window.dispatchEvent(
    new CustomEvent("venue-context-updated", {
      detail: {
        venueId: venueId.value,

        businessDate: tradingDate.value,

        tradingDate: tradingDate.value,

        weekEnding: weekEnding.value,

        weekEndingDate: weekEnding.value,

        pettyCash: rounded,
      },
    })
  );
}

async function reloadFromApi() {
  error.value = "";

  if (!ready.value) {
    return;
  }

  const selectedWeek = clean(activeWeekEnding.value);

  if (!validISODate(selectedWeek)) {
    return;
  }

  const sequence = ++loadSequence;

  loading.value = true;

  try {
    const res = await pettyCashWeekResolveRows({
      venue_id: venueId.value,

      week_ending_date: selectedWeek,
    });

    if (sequence !== loadSequence) {
      return;
    }

    rows.value = Array.isArray(res?.rows) ? res.rows : [];

    if (shouldPushTotalToApp()) {
      pushPettyCashTotalToApp(displayTotal.value);
    }
  } catch (e) {
    if (sequence !== loadSequence) {
      return;
    }

    console.error("[PettyCashView] Failed to load:", e);

    rows.value = [];

    error.value = e?.message || String(e);
  } finally {
    if (sequence === loadSequence) {
      loading.value = false;
    }
  }
}

async function reloadAll() {
  await loadVenueMode();

  if (isCashless.value && !validISODate(cashlessWeekEnding.value)) {
    cashlessWeekEnding.value = clean(weekEnding.value);
  }

  await reloadFromApi();
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = String(reader.result || "");

      const comma = result.indexOf(",");

      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };

    reader.onerror = () => reject(new Error("Could not read the receipt."));

    reader.readAsDataURL(file);
  });
}

async function saveEntry(form) {
  error.value = "";

  const selectedWeek = clean(activeWeekEnding.value);

  loading.value = true;

  try {
    if (isCashless.value) {
      const receipt = form?.receiptFile;

      if (!receipt) {
        throw new Error("A receipt is required.");
      }

      const receiptBase64 = await fileToBase64(receipt);

      const response = await generateWeeklyPack({
        action: "save_cashless_petty_cash",

        venueId: venueId.value,

        weekEnding: selectedWeek,

        expenseDate: toISODate(form.expenseDate),

        description: String(form.description || "").trim(),

        amount: Number(Number(form.amount || 0).toFixed(2)),

        category: String(form.category || "").trim(),

        receiptFileName: receipt.name,

        receiptContentType: receipt.type,

        receiptBase64,
      });

      if (response?.data?.ok !== true) {
        throw new Error("Failed to save expense.");
      }

      showToast("Expense and receipt saved.");

      await reloadFromApi();

      return;
    }

    await pettyCashWeekUpsert({
      venue_id: venueId.value,

      week_ending_date: selectedWeek,

      description: String(form.description || "").trim(),

      amount: Number(Number(form.amount || 0).toFixed(2)),

      category: String(form.category || "").trim(),

      expense_date: toISODate(form.expenseDate),

      is_claimed_back: false,

      is_calimed_back: false,
    });

    showToast("Petty Cash expense saved.");

    await reloadFromApi();
  } catch (e) {
    console.error("[PettyCashView] Save failed:", e);

    error.value = e?.message || String(e);
  } finally {
    loading.value = false;
  }
}

async function onToggleApproval(payload) {
  error.value = "";

  if (!isOperations.value) {
    error.value = "Only Operations users can approve Petty Cash items.";

    return;
  }

  const entryId = clean(payload?.id);

  const sourceWeek = clean(
    payload?._source_week_ending_date ||
      payload?.source_week_ending_date ||
      payload?.week_ending_date ||
      activeWeekEnding.value
  );

  if (!entryId || !sourceWeek) {
    error.value = "Missing Petty Cash entry details.";

    return;
  }

  const approved = payload?.is_approved === true;

  const row = rows.value.find(
    (item) =>
      String(item?.id || "") === entryId && sourceWeekEndingForRow(item) === sourceWeek
  );

  const previousValue = row?.is_approved === true;

  if (row) {
    row.is_approved = approved;
  }

  try {
    const response = await generateWeeklyPack({
      action: "set_cashless_petty_cash_approval",

      venueId: venueId.value,

      weekEnding: sourceWeek,

      entryId,

      approved,
    });

    if (response?.data?.ok !== true) {
      throw new Error("Approval update failed.");
    }
  } catch (e) {
    console.error("[PettyCashView] Approval failed:", e);

    if (row) {
      row.is_approved = previousValue;
    }

    error.value = e?.message || String(e);
  }
}

async function printPettyCashReport() {
  error.value = "";

  const selectedWeek = clean(activeWeekEnding.value);

  if (!validISODate(selectedWeek)) {
    error.value = "No valid Petty Cash week selected.";

    return;
  }

  printingPettyCash.value = true;

  const reportWindow = window.open("", "_blank");

  try {
    const response = await generateWeeklyPack({
      venueId: venueId.value,

      displayedWeekEnding: selectedWeek,

      weekEnding: selectedWeek,

      reportType: "petty_cash",
    });

    const data = response?.data || {};

    const url = data.downloadUrl || data.url || "";

    if (!url) {
      throw new Error("Report generated but no PDF URL was returned.");
    }

    if (reportWindow) {
      reportWindow.location.href = url;
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }

    showToast(`Petty Cash report generated for W/E ${selectedWeek}.`);
  } catch (e) {
    if (reportWindow && !reportWindow.closed) {
      reportWindow.close();
    }

    console.error("[PettyCashView] Report failed:", e);

    error.value = e?.message || String(e);
  } finally {
    printingPettyCash.value = false;
  }
}

async function onToggleClaimed(payload) {
  error.value = "";

  const sourceWeek = clean(
    payload?._source_week_ending_date ||
      payload?.source_week_ending_date ||
      payload?.week_ending_date ||
      activeWeekEnding.value
  );

  loading.value = true;

  try {
    await pettyCashWeekUpsert({
      venue_id: venueId.value,

      week_ending_date: sourceWeek,

      id: String(payload.id),

      expense_date: String(payload.expense_date),

      description: String(payload.description),

      amount: Number(payload.amount || 0),

      category: String(payload.category),

      is_claimed_back: Boolean(payload.is_claimed_back),

      is_calimed_back: Boolean(payload.is_claimed_back),
    });

    showToast(
      isCashless.value
        ? "Expense marked as reimbursed / settled."
        : "Expense marked as claimed back."
    );

    await reloadFromApi();
  } catch (e) {
    error.value = e?.message || String(e);
  } finally {
    loading.value = false;
  }
}

watch(
  [venueId, tradingDate, weekEnding],

  async (
    [nextVenue, nextDate, nextWeek],

    previous = []
  ) => {
    const [previousVenue, previousDate, previousWeek] = previous;

    if (!nextVenue || !nextDate || !nextWeek) {
      return;
    }

    const venueChanged = nextVenue !== previousVenue;

    const dateChanged = nextDate !== previousDate;

    const weekChanged = nextWeek !== previousWeek;

    if (!venueChanged && !dateChanged && !weekChanged) {
      return;
    }

    if (venueChanged) {
      rows.value = [];

      cashlessWeekEnding.value = clean(nextWeek);

      await loadVenueMode();

      await reloadFromApi();

      return;
    }

    if (weekChanged) {
      cashlessWeekEnding.value = clean(nextWeek);
    }

    await reloadFromApi();
  },

  {
    immediate: true,
  }
);

onMounted(async () => {
  await resolveOperationsAccess();
});
</script>

<style scoped>
.cashless-heading {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cashless-heading .material-icons {
  font-size: 22px;
}
</style>
